import { memo, useCallback, useMemo, useState } from "react";
import { useRouteLoaderData } from "@remix-run/react";

import { track } from "~/utils/track";
import { logger } from "~/utils/logging";
import { formatTimestamp, localizeDate, localizeTime } from "~/utils/helpers";
import { toApiNoteColor, toNoteColor } from "~/utils/api";
import { useLabels } from "~/hooks/useLabels";
import { useRouteAction } from "~/hooks/useRouteAction";
import { NoteCard } from "~/components/noteCard/NoteCard";
import { NoteEditorModal } from "~/components/noteEditorModal/NoteEditorModal";
import { DEFAULT_NOTE_CHARACTER_LIMIT, Action } from "~/constants";

import type { Note } from "~/graphql/types";
import type { ActionProp } from "~/components/components";
import type { NoteEditorModalSaveEvent } from "~/components/noteEditorModal/NoteEditorModal";
import type { ColorPickerChangeEvent } from "~/components/colorPicker/ColorPicker";
import type { SessionRouteLoaderData } from "./route";

export interface SessionNotesProps {
  onRequestSeek?: (timestamp: number) => void;
}

export const SessionNotes = memo(function SessionNotes({
  onRequestSeek,
}: SessionNotesProps) {
  const { t } = useLabels();

  const data = useRouteLoaderData(
    "routes/$class_.sessions.$session",
  ) as SessionRouteLoaderData;

  const { intent, fetcher, submitAction } = useRouteAction();

  const [editingNoteId, setEditingNoteId] = useState("");

  const [deletedNoteIds, setDeletedNoteIds] = useState<string[]>([]);

  const trackingData = useMemo(
    () => ({
      className: data.session.class?.slug,
      sessionNumber: data.session.position,
      trigger: "sessionNotebook",
    }),
    [data.session.class?.slug, data.session.position],
  );

  const handleRequestEditorClose = useCallback(() => {
    setEditingNoteId("");
  }, []);

  const renderNote = useCallback(
    (n: Note) => {
      const startTime = Math.max(n.timestamp - 60, 0);
      const endTime = n.timestamp;

      const modalTitle = t("note.title", {
        number: n.session?.position,
        startTime: formatTimestamp(startTime),
        endTime: formatTimestamp(endTime),
      });

      const modalPostTitle = t("note.postTitle", {
        date: localizeDate(n.createdAt),
        time: localizeTime(n.createdAt),
      });

      const isMutating = fetcher.formData?.get("noteId") === n.id;

      const isFavoritedOptimistic =
        isMutating &&
        [Action.FavoriteNote, Action.UnfavoriteNote].includes(intent as Action)
          ? intent === Action.FavoriteNote
          : Boolean(n.isFavorited);

      const isDeleting = deletedNoteIds.includes(n.id);

      const colorFromIntent =
        isMutating && intent === Action.UpdateNote
          ? (JSON.parse(fetcher.formData?.get("input") as string)
              ?.color as string)
          : undefined;

      const colorOptimistic = toNoteColor(colorFromIntent ?? n.color);

      async function handleFavorite() {
        await submitAction(
          {
            intent: n.isFavorited ? Action.UnfavoriteNote : Action.FavoriteNote,
            noteId: n.id,
            input: {
              isFavorited: !n.isFavorited,
            },
          },
          { authRequired: true },
        );

        if (!n.isFavorited) {
          track("crNoteFavorite", trackingData);
        }
      }

      async function handleColorChange(e: ColorPickerChangeEvent) {
        await submitAction({
          intent: Action.UpdateNote,
          noteId: n.id,
          input: {
            color: e.value.toUpperCase(),
          },
        });
      }

      async function handleSave(e: NoteEditorModalSaveEvent) {
        await submitAction({
          intent: Action.UpdateNote,
          noteId: n.id,
          input: {
            content: e.value,
            color: toApiNoteColor(e.color),
          },
        });

        handleRequestEditorClose();
      }

      async function handleTimestampClick() {
        onRequestSeek?.(startTime);
      }

      const actions: ActionProp[] = [
        {
          icon: "star",
          iconVariant: isFavoritedOptimistic ? "solid" : "regular",
          text: isFavoritedOptimistic
            ? t("note.cta.unfavorite")
            : t("note.cta.favorite"),
          onClick() {
            handleFavorite().catch((error) => {
              logger.error(error);
            });
          },
        },
        {
          icon: "pencil",
          text: t("note.cta.edit"),
          onClick() {
            setEditingNoteId(n.id);
          },
        },
        {
          icon: "trash-can",
          text: isDeleting ? t("note.cta.deleting") : t("note.cta.delete"),
          async onClick() {
            setDeletedNoteIds((i) => i.concat(n.id));

            await submitAction({
              intent: Action.DeleteNote,
              noteId: n.id,
            });

            track("crNoteDelete", trackingData);
          },
        },
      ];

      return (
        <div key={n.id}>
          <NoteCard
            actions={actions}
            color={colorOptimistic}
            content={n.contentHtml}
            isFavorited={isFavoritedOptimistic}
            modalPostTitle={modalPostTitle}
            modalTitle={modalTitle}
            startTime={startTime}
            endTime={endTime}
            onColorChange={handleColorChange}
            onFavorite={handleFavorite}
            onTimestampClick={handleTimestampClick}
          />

          <NoteEditorModal
            color={colorOptimistic}
            id={n.id}
            initialValue={n.content}
            isOpen={editingNoteId === n.id}
            limit={DEFAULT_NOTE_CHARACTER_LIMIT}
            title={t("note.cta.edit")}
            onSave={handleSave}
            onCancel={handleRequestEditorClose}
            onRequestClose={handleRequestEditorClose}
          />
        </div>
      );
    },
    [
      onRequestSeek,
      deletedNoteIds,
      editingNoteId,
      fetcher.formData,
      handleRequestEditorClose,
      intent,
      submitAction,
      t,
      trackingData,
    ],
  );

  return <>{data.notes?.map(renderNote)}</>;
});
